import '../notice.css';
import '../extra.css';
import IdCard from '../components/IdCard.jsx';
import ManseTable from '../components/ManseTable.jsx';
import ClimateGauge from '../components/ClimateGauge.jsx';
import SceneView from '../components/SceneView.jsx';
import { natalGap } from '../lib/natalGap.js';
import { WindowTitle } from '../components/Desktop.jsx';

const GROUP_LABEL = { 비겁: '관계', 식상: '활동', 재성: '재물', 관성: '직업', 인성: '학업' };
const GROUP_MEANING = {
  비겁: '나와 같은 기운. 친구·동료와 어울리는 힘',
  식상: '내가 내보내는 기운. 표현하고 만들어 내는 힘',
  재성: '내가 다루는 기운. 돈과 현실을 챙기는 힘',
  관성: '나를 다듬는 기운. 조직과 책임을 맡는 힘',
  인성: '나를 채우는 기운. 배우고 익히는 힘',
};
const TEMPER = [['목', '배려'], ['화', '열정'], ['토', '안정'], ['금', '원칙'], ['수', '신중']];
const STEM_KO = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const TEMPER_NOTE = {
  배려: '남의 사정을 먼저 살피는 쪽으로 기울어요',
  열정: '해 보고 싶은 쪽으로 먼저 움직여요',
  안정: '무리하지 않고 지키는 쪽을 골라요',
  원칙: '기준과 약속을 따르는 쪽을 골라요',
  신중: '충분히 알아본 뒤 움직이는 쪽을 골라요',
};

function birthLine(run) {
  const { saju, birth } = run;
  const [y, m, d] = saju.solarDate.split('-').map(Number);
  const time = birth?.hour == null ? '태어난 시각은 모릅니다' : `${birth.hour}시 ${String(birth.minute ?? 0).padStart(2, '0')}분`;
  const who = birth?.gender === 'F' ? '여자' : '남자';
  return `${y}년 ${m}월 ${d}일(음력 ${saju.lunarDate}), ${time}. ${who}아이로 태어났습니다.`;
}

export default function RevealPage({ run, onStart, onReset }) {
  const { saju } = run;
  const [y, m, d] = saju.solarDate.split('-');
  const no = `SJ-${y}${m}${d}-${String(run.seed % 10000).padStart(4, '0')}`;
  const top = Math.max(...Object.values(saju.elements));
  const topTemper = TEMPER.filter(([el]) => saju.elements[el] === top);
  const dayStem = saju.day.stem;
  const basis = saju.statBasis;
  const gaps = natalGap(saju);
  const hourUnknown = !saju.hour;
  const rankedStats = Object.entries(saju.stats).sort((a, b) => b[1] - a[1]);

  return (
    <div className="card wide notice reveal-window">
      <WindowTitle label="BIRTH RECORD">출생 신고</WindowTitle>
      <div className="reveal-content">
      <div className="notice-head">
        <span className="chip">출생 신고</span><span className="muted">No. {no}</span>
      </div>
      <p className="notice-line">{birthLine(run)}</p>
      <IdCard run={run} />

      <section className="reveal-section">
      <h2>사주 원국</h2>
      <ManseTable saju={saju} />
      <details className="reveal-help"><summary>원국 설명</summary>
      <p className="muted">
        사주는 태어난 해, 달, 날, 시를 글자 두 개씩, 모두 여덟 글자로 적은 표예요.
        가운데 "태어난 날"의 윗글자 <b>{STEM_KO[dayStem]}</b>가 바로 '나'예요. 나머지 글자는 나랑 어떤 사이인지로 읽어요.
        {hourUnknown && ' 태어난 시간을 모르면 그 칸은 비워 두고, 열두 시간의 평균으로 점수를 정해요.'}
      </p>
      </details>
      <ClimateGauge saju={saju} />
      </section>

      <section className="reveal-section reveal-stats">
      <h2>타고난 영역</h2>
      <details className="reveal-help"><summary>능력치 기준</summary>
      <p className="muted">
        사주의 기운을 다섯 영역(관계·활동·재물·직업·학업)으로 묶어 능력치로 써요. <b>원국 세력</b>은 사주 안에서 그 영역이 차지하는 비중이고,
        막대와 숫자는 게임을 시작할 때 쓰는 <b>출발 능력</b>이에요. 수치가 높다고 결과가 정해지는 건 아니고, 시작점일 뿐이에요.
      </p>
      </details>
      <div className="natal-stat-grid">
      {rankedStats.map(([g, v]) => {
        const rank = rankedStats.findIndex(([, score]) => score === v) + 1;
        const gap = gaps?.find((row) => row.field === g);
        return (
          <div key={g} className="natal-stat">
            <div className="bar">
              <span>{rank}위 · {GROUP_LABEL[g]}</span>
              <div className="track"><div style={{ width: `${v}%` }} /></div>
              <b>{v}</b>
            </div>
            {gap && <p className="natal-share">원국 세력 {Math.round(gap.share * 100)}% · {gap.forceRank}위</p>}
            {gap?.note && <p className="natal-note">{gap.note}</p>}
          </div>
        );
      })}
      </div>
      <details className="basis">
        <summary>각 영역은 무슨 뜻인가요?</summary>
        <ul className="meaning">
          {Object.keys(saju.stats).map((g) => <li key={g}><b>{GROUP_LABEL[g]}</b> · {GROUP_MEANING[g]}</li>)}
        </ul>
      </details>
      </section>

      <section className="reveal-section reveal-temper">
      <h2>타고난 성향</h2>
      <div className="row">
        {TEMPER.map(([el, name]) => (
          <div key={el} className={`temper ${saju.elements[el] === top ? 'hl' : ''}`}>
            <b>{name}</b><br />{el} {saju.elements[el]}%
          </div>
        ))}
      </div>
      <details className="reveal-help"><summary>성향 설명</summary><p className="muted">
        성향은 타고난 기운(목·화·토·금·수)의 비율로 정해져요.{' '}
        {topTemper.length === 1
          ? `가장 높은 건 ${topTemper[0][1]}(${topTemper[0][0]})이고, ${TEMPER_NOTE[topTemper[0][1]]}.`
          : `가장 높은 ${topTemper.map(([el, name]) => `${name}(${el})`).join('·')}이 똑같아서, 하나로 정하지 않고 모두 가까워요.`}
        {' '}'자동 결정'이나 4단계 직업 길을 고를 때 이 쪽으로 마음이 기울어요.
      </p>
      </details>
      </section>

      {basis && (
        <details className="basis">
          <summary>이 점수는 어떻게 나왔나요?</summary>
          <p className="muted">사주의 합(글자끼리 어울리는 작용)과 조후(태어난 계절의 더위·추위)를 반영해서 계산한 결과예요.</p>
          <p className="muted">원국 세력이 큰데 출발 능력이 낮은 영역은, 나(일간)를 받쳐 주는 힘이 적을 때 활동·재물·직업을 낮추고 힘이 충분할 때 관계·학업을 낮추는 이 게임의 규칙 때문이에요(게임 추정, 고서의 수치가 아니에요).</p>
          {hourUnknown && basis.hourUncertainty?.ranges && (
            <>
              <p className="muted">태어난 시간을 몰라서 점수가 아래 범위에서 달라질 수 있어요. 게임에는 가운데 값을 써요.</p>
              <ul className="meaning">
                {Object.entries(basis.hourUncertainty.ranges).map(([g, r]) => (
                  <li key={g}>{GROUP_LABEL[g] ?? g}: {r.min} ~ {r.max} (가운데 {r.mean})</li>
                ))}
              </ul>
            </>
          )}
        </details>
      )}

      <section className="reveal-section reveal-landscape">
      <h2>풍경</h2>
      <SceneView saju={saju} />
      <p className="muted small">풍경 · 월지 / 날씨 · 월간 / 시간대 · 시지 / 동물 · 일지, 색 · 일간</p>
      </section>

      </div>
      <div className="reveal-actions">
        <button onClick={onReset}>다시 입력</button>
        <button className="primary" onClick={onStart}>시작하기 <span aria-hidden="true">↗</span></button>
      </div>
    </div>
  );
}
